import React, { useState } from 'react'
import { Box, Chip, Tab, Tabs, Typography } from '@material-ui/core'
import LanguageIcon from '@material-ui/icons/Language'
import OpenInNewIcon from '@material-ui/icons/OpenInNew'
import EditIcon from '@material-ui/icons/Edit'
import PeopleIcon from '@material-ui/icons/People'
import BarChartIcon from '@material-ui/icons/BarChart'

import { Method, PublishedTab, SiteState } from './types'
import { GEN_STEPS, SECTIONS } from './websiteData'
import { useWebsiteStyles } from './useWebsiteStyles'
import { NoSiteView } from './components/NoSiteView'
import { CreatingView } from './components/CreatingView'
import { SiteEditor } from './components/SiteEditor'
import { VisitorTracking } from './components/VisitorTracking'
import { Analytics } from './components/Analytics'

const WEBSITE_TABS = [
    { id: 'site' as const, label: 'Site Editor', icon: EditIcon },
    { id: 'visitors' as const, label: 'Visitor Tracking', icon: PeopleIcon },
    { id: 'analytics' as const, label: 'Analytics', icon: BarChartIcon },
]

export const WebsiteHome: React.FC = () => {
    const classes = useWebsiteStyles()
    const [state, setState] = useState<SiteState>('published')
    const [method, setMethod] = useState<Method | null>(null)
    const [genStep, setGenStep] = useState(0)
    const [aiEdit, setAiEdit] = useState('')
    const [sections, setSections] = useState(SECTIONS)
    const [tab, setTab] = useState<PublishedTab>('site')

    const start = (m: Method) => {
        setMethod(m)
        setState('creating')
        setGenStep(0)
        let i = 0
        const tick = () => {
            i++
            setGenStep(i)
            if (i < GEN_STEPS.length) {
                setTimeout(tick, 500)
            } else {
                setTimeout(() => setState('published'), 400)
            }
        }
        setTimeout(tick, 500)
    }

    const handleTabChange = (
        _event: React.ChangeEvent<{}>,
        newValue: PublishedTab,
    ) => {
        setTab(newValue)
    }

    return (
        <Box className={classes.root}>
            <Box className={classes.pageHeader}>
                <Box className={classes.headerLeft}>
                    <Box className={classes.titleRow}>
                        <Box className={classes.titleIcon}>
                            <LanguageIcon fontSize="small" />
                        </Box>
                        <Box>
                            <Typography variant="h6">Website</Typography>
                            <Typography className={classes.pageSubtitle}>
                                Build, edit, and deploy your leasing site. Track
                                every visitor — even the anonymous ones.
                            </Typography>
                        </Box>
                    </Box>
                </Box>

                <Box className={classes.headerRight}>
                    {state === 'published' && (
                        <Box className={classes.headerActions}>
                            <a href="#" className={classes.visitLink}>
                                Visit site
                                <OpenInNewIcon style={{ fontSize: 14 }} />
                            </a>
                            <Chip
                                size="small"
                                className={classes.liveChip}
                                label={
                                    <Box display="flex" alignItems="center">
                                        <span className={classes.liveDot} />
                                        Live
                                    </Box>
                                }
                            />
                        </Box>
                    )}
                </Box>
            </Box>

            {state === 'published' && (
                <Box className={classes.tabsWrapper}>
                    <Tabs
                        value={tab}
                        onChange={handleTabChange}
                        variant="fullWidth"
                        className={classes.tabs}
                        TabIndicatorProps={{ style: { display: 'none' } }}
                    >
                        {WEBSITE_TABS.map((t) => {
                            const Icon = t.icon
                            return (
                                <Tab
                                    key={t.id}
                                    className={classes.tab}
                                    value={t.id}
                                    label={
                                        <Box className={classes.tabLabel}>
                                            <Icon className={classes.tabIcon} />
                                            <span>{t.label}</span>
                                        </Box>
                                    }
                                />
                            )
                        })}
                    </Tabs>
                </Box>
            )}

            <Box className={classes.content}>
                {state === 'no_site' && <NoSiteView onPick={start} />}

                {state === 'creating' && method && (
                    <CreatingView method={method} step={genStep} />
                )}

                {state === 'published' && (
                    <>
                        {tab === 'site' && (
                            <SiteEditor
                                aiEdit={aiEdit}
                                setAiEdit={setAiEdit}
                                sections={sections}
                                setSections={setSections}
                            />
                        )}
                        {tab === 'visitors' && <VisitorTracking />}
                        {tab === 'analytics' && <Analytics />}
                    </>
                )}
            </Box>
        </Box>
    )
}
